/**
 * Monitor Dashboard v2 — Venue-centric view with drill-down
 * Alternative to MonitoringDashboard: All Venues grid → Single Venue detail (tabs)
 * See docs/MONITOR_DASHBOARD_V2_COMPARISON.md
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  AlertTriangle, CheckCircle2, Clock, Database, FileText,
  Activity, ChevronLeft, ChevronRight, Upload, Zap, Eye,
  TrendingUp, Users, Shield, RefreshCw, Building2, BarChart2,
  Bell, ArrowRight
} from 'lucide-react'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { readApiAccessMessage } from '../../utils/apiAccessError'

import { formatSydneyDateTime } from '../../utils/sydneyDateTime'
const EMPTY_ALERTS = { critical: 0, high: 0, medium: 0, low: 0, total: 0, unreviewed: 0, newToday: 0 }

const F = { sans: "'Poppins', system-ui, sans-serif", mono: "'Poppins', system-ui, sans-serif" }
const C = {
  pri: '#0a5169', priD: '#07384a', priL: '#1a7a9b', priM: '#e8f2f6',
  red: '#dc2626', redBg: '#fef2f2', redBd: '#fecaca',
  amber: '#d97706', amberBg: '#fffbeb', amberBd: '#fde68a',
  green: '#16a34a', greenBg: '#f0fdf4', greenBd: '#bbf7d0',
  purple: '#7c3aed', purpleBg: '#f5f3ff', purpleBd: '#ddd6fe',
  orange: '#ea580c', orangeBg: '#fff7ed', orangeBd: '#fed7aa',
  slate: '#64748b', slateL: '#94a3b8', slateBg: '#f8fafc',
  ink: '#0f172a', border: '#e2e8f0',
}

function StatCard({ label, value, sub, color = C.ink, icon: Icon, trend }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
        {Icon && <div style={{ width: 28, height: 28, borderRadius: 7, background: (color || C.pri) + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} color={color || C.pri} />
        </div>}
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, color, fontFamily: F.mono, lineHeight: 1, marginBottom: sub ? 5 : 0 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: C.slateL, display: 'flex', alignItems: 'center', gap: 4 }}>
        {trend === 'up' && <TrendingUp size={12} color={C.red} />}
        {sub}
      </div>}
    </div>
  )
}

function SectionHead({ icon: Icon, title, sub, right, color = C.pri }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} color={color} />
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.ink }}>{title}</div>
          {sub && <div style={{ fontSize: 12, color: C.slateL }}>{sub}</div>}
        </div>
      </div>
      {right}
    </div>
  )
}

function HealthDot({ status }) {
  const map = { healthy: C.green, warning: C.amber, error: C.red, stale: C.slateL }
  return <div style={{ width: 8, height: 8, borderRadius: '50%', background: map[status] || C.slateL, flexShrink: 0 }} />
}

function PillBadge({ label, color, bg }) {
  return <span style={{ fontSize: 12, fontWeight: 700, color, background: bg, padding: '2px 9px', borderRadius: 20, border: `1px solid ${color}33` }}>{label}</span>
}

function deriveVenueHealth(stats) {
  if (stats?.healthStatus) return stats.healthStatus
  const alerts = stats?.alerts || {}
  if ((alerts.critical || 0) > 0) return 'error'
  if ((alerts.high || 0) > 0 || (alerts.unreviewed || 0) > 5) return 'warning'
  return 'healthy'
}

function formatPipelineTime(iso, timezone) {
  if (!iso) return '—'
  try {
    return formatSydneyDateTime(new Date(iso))
  } catch {
    return String(iso)
  }
}

function pipelineFeedStyle(status) {
  if (status === 'ingested') return { color: C.green, bg: C.greenBg, bd: C.greenBd, headline: 'Ingested today' }
  if (status === 'failed') return { color: C.red, bg: C.redBg, bd: C.redBd, headline: 'Last run failed' }
  if (status === 'running') return { color: C.pri, bg: C.priM, bd: C.border, headline: 'Import running' }
  if (status === 'missing') return { color: C.amber, bg: C.amberBg, bd: C.amberBd, headline: 'Not ingested today' }
  if (status === 'disabled') return { color: C.slateL, bg: C.slateBg, bd: C.border, headline: 'Disabled' }
  return { color: C.slate, bg: C.slateBg, bd: C.border, headline: status || 'Unknown' }
}

function dataHealthBanner(dataHealth) {
  if (dataHealth === 'error') {
    return { color: C.red, bg: C.redBg, bd: C.redBd, text: 'Data pipeline error — imports failed or rules run failed. Review Daily Data feeds.' }
  }
  if (dataHealth === 'stale') {
    return { color: C.amber, bg: C.amberBg, bd: C.amberBd, text: 'Data pipeline stale — expected imports or rules run missing for today.' }
  }
  if (dataHealth === 'warning') {
    return { color: C.amber, bg: C.amberBg, bd: C.amberBd, text: 'Data pipeline warning — partial import errors or rules run overdue.' }
  }
  return null
}

function PipelineFeedCard({ feed, icon: Icon, accent, detailHref }) {
  if (!feed) return null
  const st = pipelineFeedStyle(feed.status)
  const success = feed.lastSuccess
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
      <SectionHead icon={Icon} title={feed.label} sub={feed.enabled ? feed.statusLabel : 'Feed disabled'} color={accent} />
      <div style={{ background: st.bg, border: `1px solid ${st.bd}`, borderRadius: 8, padding: '12px 14px', marginBottom: 14, display: 'flex', gap: 9, alignItems: 'center' }}>
        {feed.status === 'ingested' ? <CheckCircle2 size={16} color={st.color} /> : <AlertTriangle size={16} color={st.color} />}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: st.color }}>{st.headline}</div>
          <div style={{ fontSize: 11, color: C.slate }}>
            {formatPipelineTime(feed.lastRunAt)} {success?.filePath ? `· ${success.filePath.split('/').pop()}` : ''}
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { label: 'Imported', value: success?.imported ?? '—' },
          { label: 'Errors', value: success?.errors ?? '—', warn: (success?.errors || 0) > 0 },
          { label: 'Trading day', value: success?.tradingDay ? String(success.tradingDay).slice(0, 10) : '—' },
        ].map((s) => (
          <div key={s.label} style={{ background: C.slateBg, borderRadius: 7, padding: '10px 12px' }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: s.warn ? C.red : C.ink, fontFamily: F.mono }}>{s.value}</div>
            <div style={{ fontSize: 11, color: C.slateL, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {detailHref && (
        <Link to={detailHref} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '9px 0', background: accent, color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
          <Database size={13} /> Open in Daily Data
        </Link>
      )}
    </div>
  )
}

function DataPipelinePanel({ pipeline }) {
  if (!pipeline) {
    return (
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '24px', textAlign: 'center', color: C.slateL, fontSize: 13 }}>
        Pipeline status unavailable for this tenant.
      </div>
    )
  }
  const { feeds, rulesRun, timezone, tradingDayToday } = pipeline
  const feedLinks = {
    patron: '/monitor-safe/daily-data/dimpatron',
    sessions: '/monitor-safe/daily-data/sessions',
    meter: '/monitor-safe/daily-data/meter',
  }
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap', fontSize: 12, color: C.slate }}>
        <span>Trading day: <strong>{tradingDayToday || '—'}</strong></span>
        <span>Timezone: {timezone || 'Australia/Sydney'}</span>
        {rulesRun?.at && (
          <span>Last rules run: <strong>{formatPipelineTime(rulesRun.at, timezone)}</strong>{rulesRun.label ? ` (${rulesRun.label})` : ''}</span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 14 }}>
        <PipelineFeedCard feed={feeds?.sessions} icon={Activity} accent={C.pri} detailHref={feedLinks.sessions} />
        <PipelineFeedCard feed={feeds?.meter} icon={BarChart2} accent={C.purple} detailHref={feedLinks.meter} />
        <PipelineFeedCard feed={feeds?.patron} icon={Users} accent={C.priL} detailHref={feedLinks.patron} />
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
          <SectionHead icon={Zap} title="Rules engine" sub={rulesRun?.at ? formatPipelineTime(rulesRun.at, timezone) : 'No recent run recorded'} />
          <div style={{ fontSize: 12, color: C.slateL, lineHeight: 1.6, marginBottom: 12 }}>
            {rulesRun?.source === 'none'
              ? 'No rules batch or scheduled run found. Use Rule Scheduler or process rules after session import.'
              : `${rulesRun?.label || 'Rules run'}${rulesRun?.status ? ` · ${rulesRun.status}` : ''}${rulesRun?.recordsProcessed != null ? ` · ${rulesRun.recordsProcessed} records` : ''}${rulesRun?.alertsGenerated != null ? ` · ${rulesRun.alertsGenerated} alerts` : ''}`}
          </div>
          <Link to="/monitor-safe/rule-scheduler" style={{ fontSize: 12, color: C.pri, fontWeight: 600, textDecoration: 'none' }}>Open Rule Scheduler →</Link>
        </div>
      </div>
      <div style={{ background: C.priM, border: `1px solid ${C.pri}33`, borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.pri, marginBottom: 6 }}>Tenant-wide imports</div>
        <div style={{ fontSize: 12, color: C.slate, lineHeight: 1.5 }}>
          Daily Data feeds apply to the whole tenant — not per venue. Manage schedules, manual runs, and Azure extracts in Daily Data.
        </div>
        <Link to={pipeline.dailyDataHref || '/monitor-safe/daily-data'} style={{ display: 'inline-block', marginTop: 10, fontSize: 12, fontWeight: 700, color: C.pri, textDecoration: 'none' }}>
          Daily Data hub →
        </Link>
      </div>
    </div>
  )
}

function mapVenueStatsRow(row) {
  return {
    venueId: row.venueId,
    healthStatus: row.healthStatus || 'healthy',
    alerts: row.alerts || EMPTY_ALERTS,
    watchlist: row.watchlist || { total: 0, highRisk: 0, ecddOpen: 0, newThisWeek: 0 },
    smr: row.smr || { active: 0, overdue: 0, dueSoon: 0 },
    ruleHits: (row.ruleHits || []).map((r) => ({
      ...r,
      color: r.color || C.orange,
    })),
    topPatrons: (row.topPatrons || []).map((p) => ({
      ...p,
      id: p.id || p.memberId || p.member_id,
      member_id: p.member_id || p.memberId,
      risk: p.risk ?? p.riskScore ?? null,
    })),
    activityTrend: row.activityTrend || [],
  }
}

/* ══════════════════════════════════════════════════════
   ALL VENUES VIEW
══════════════════════════════════════════════════════ */
function AllVenuesView({ venues, venueStats, portfolioAlerts, portfolioWatchlist, pipeline, onSelect }) {
  const totalAlerts = portfolioAlerts?.total ?? 0
  const totalUnreviewed = portfolioAlerts?.unreviewed ?? 0
  const totalWatchlist = portfolioWatchlist?.total ?? 0
  const totalSMRActive = venueStats.reduce((s, v) => s + (v?.smr?.active || 0), 0)
  const totalOverdue = venueStats.reduce((s, v) => s + (v?.smr?.overdue || 0), 0)
  const totalCriticalHigh = (portfolioAlerts?.critical ?? 0) + (portfolioAlerts?.high ?? 0)
  const pipelineBanner = dataHealthBanner(pipeline?.dataHealth)

  return (
    <div>
      {pipelineBanner && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: pipelineBanner.bg, border: `1px solid ${pipelineBanner.bd}`, borderRadius: 8, color: pipelineBanner.color, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span>{pipelineBanner.text}</span>
          <Link to={pipeline?.dailyDataHref || '/monitor-safe/daily-data'} style={{ fontSize: 12, fontWeight: 700, color: pipelineBanner.color, textDecoration: 'none', whiteSpace: 'nowrap' }}>Daily Data →</Link>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        <StatCard label="Total Alerts" value={totalAlerts} sub={`${totalUnreviewed} unreviewed`} color={C.orange} icon={Bell} trend={totalUnreviewed > 0 ? 'up' : null} />
        <StatCard label="Critical + High" value={totalCriticalHigh} sub="Needs priority review" color={C.red} icon={AlertTriangle} />
        <StatCard label="Watchlist" value={totalWatchlist} sub={`${portfolioWatchlist?.highRisk ?? 0} high risk · tenant-wide`} color={C.purple} icon={Eye} />
        <StatCard label="SMR Active" value={totalSMRActive} sub={totalOverdue > 0 ? `${totalOverdue} overdue` : 'All within deadline'} color={totalOverdue > 0 ? C.red : C.green} icon={FileText} />
        <StatCard label="Venues" value={`${venues.length}`} sub={venues.length ? 'from API' : 'none returned'} color={C.green} icon={Building2} />
      </div>

      {venues.length === 0 ? (
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: '28px 24px', textAlign: 'center', color: C.slate, fontSize: 14 }}>
          No venues from <code style={{ background: C.slateBg, padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>/api/venues</code>.
          Seed venues for this tenant or confirm your role can list venues.
        </div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
        {venues.map((v) => {
          const stats = venueStats.find(s => s.venueId === v.venue_id || s.venueId === v.id) || {}
          const alerts = stats.alerts || { critical: 0, high: 0, medium: 0, low: 0, total: 0, unreviewed: 0, newToday: 0 }
          const watchlist = stats.watchlist || { total: 0, highRisk: 0, ecddOpen: 0, newThisWeek: 0 }
          const smr = stats.smr || { active: 0, overdue: 0, dueSoon: 0 }
          const urgencyColor = alerts.critical > 0 ? C.red : alerts.high > 0 ? C.orange : alerts.medium > 0 ? C.amber : C.green

          return (
            <div key={v.venue_id || v.id} onClick={() => onSelect(v)}
              style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', cursor: 'pointer', transition: 'box-shadow 0.15s', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div>
                  <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 3 }}>
                    <HealthDot status={deriveVenueHealth(stats)} />
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>{v.name}</div>
                  </div>
                  <div style={{ fontSize: 12, color: C.slateL }}>
                    {v.code || v.venue_id} · CO: {v.co || '—'}
                    {pipeline?.feeds?.sessions?.enabled && (
                      <span> · Sessions: {pipeline.feeds.sessions.statusLabel || pipeline.feeds.sessions.status}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {smr.overdue > 0 && <PillBadge label={`${smr.overdue} SMR overdue`} color={C.red} bg={C.redBg} />}
                  <ChevronRight size={16} color={C.slateL} />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.slateL, marginBottom: 5 }}>
                  <span>Alerts ({alerts.total})</span>
                  <span style={{ color: urgencyColor, fontWeight: 600 }}>{alerts.unreviewed} unreviewed</span>
                </div>
                <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', gap: 1 }}>
                  {[
                    { count: alerts.critical, color: C.red },
                    { count: alerts.high, color: C.orange },
                    { count: alerts.medium, color: C.amber },
                    { count: alerts.low, color: C.green },
                  ].filter(x => x.count > 0).map((x, i) => (
                    <div key={i} style={{ flex: x.count, background: x.color, borderRadius: 3 }} />
                  ))}
                  {alerts.total === 0 && <div style={{ flex: 1, background: C.border, borderRadius: 3 }} />}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  {[{ label: 'Critical', val: alerts.critical, color: C.red }, { label: 'High', val: alerts.high, color: C.orange }, { label: 'Medium', val: alerts.medium, color: C.amber }, { label: 'Low', val: alerts.low, color: C.green }].map(x => (
                    <div key={x.label} style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: x.color }} />
                      <span style={{ fontSize: 12, color: x.val > 0 ? x.color : C.slateL, fontWeight: x.val > 0 ? 700 : 400 }}>{x.val} {x.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                {[
                  { label: 'Watchlist', value: watchlist.total, sub: `${watchlist.highRisk} high risk`, color: C.purple },
                  { label: 'ECDD Open', value: watchlist.ecddOpen, sub: 'active reviews', color: C.pri },
                  { label: 'SMR Active', value: smr.active, sub: smr.overdue > 0 ? `${smr.overdue} overdue` : 'in deadline', color: smr.overdue > 0 ? C.red : C.ink },
                  { label: 'New Today', value: alerts.newToday, sub: 'alerts fired', color: alerts.newToday > 0 ? C.orange : C.ink },
                ].map((s, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: s.color, fontFamily: F.mono, lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: C.slateL, marginTop: 2, lineHeight: 1.3 }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: C.slateL }}>{s.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
      )}
    </div>
  )
}

/* ══════════════════════════════════════════════════════
   SINGLE VENUE VIEW
══════════════════════════════════════════════════════ */
function venueAlertMatchKeys(venue) {
  const keys = new Set()
  for (const raw of [
    venue?.venue_id,
    venue?.id,
    venue?.code,
    venue?.name,
    venue?.gaming_extract_venue_name,
  ]) {
    if (raw == null || String(raw).trim() === '') continue
    keys.add(String(raw).trim().toLowerCase())
  }
  return [...keys]
}

function alertMatchesVenue(alert, venue) {
  const venueId = String(venue?.venue_id ?? venue?.id ?? '').trim().toLowerCase()
  if (!venueId) return true
  const alertVenueId = String(alert?.venue_id || '').trim().toLowerCase()
  if (alertVenueId && alertVenueId === venueId) return true
  const keys = venueAlertMatchKeys(venue)
  if (!keys.length) return true
  const code = String(alert?.venue_code_raw || alert?.venue_code || '').trim().toLowerCase()
  if (!code) return false
  return keys.some((k) => code === k || code.includes(k) || k.includes(code))
}

function isUnreviewedAlert(alert) {
  const st = String(alert?.status || '').toLowerCase()
  return !['closed', 'resolved', 'dismissed'].includes(st)
}

function severityColor(sev) {
  const s = String(sev || '').toLowerCase()
  if (s === 'critical') return C.red
  if (s === 'high') return C.orange
  if (s === 'medium') return C.amber
  if (s === 'low') return C.green
  return C.slate
}

function formatAlertWhen(a) {
  const raw = a?.last_updated || a?.first_seen || a?.created_at
  if (!raw) return '—'
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return '—'
  return formatSydneyDateTime(d)
}

function ecddMatchesVenue(record, venue) {
  const keys = venueAlertMatchKeys(venue)
  if (!keys.length) return true
  const code = String(record?.venueId || record?.venue_id || '').trim().toLowerCase()
  if (!code) return false
  return keys.some((k) => code === k || code.includes(k) || k.includes(code))
}

function VenueWatchlistPanel({ venue, watchlist, topPatrons = [] }) {
  const navigate = useNavigate()
  const [ecddRows, setEcddRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const wlTotal = Number(watchlist?.total) || 0

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const ecddRes = await authFetch(getLocalApiUrl('/api/ecdd'))
        const ecddJson = await ecddRes.json().catch(() => ({}))
        if (!ecddRes.ok || !ecddJson.success) {
          throw new Error(ecddJson.error || `ECDD list failed (${ecddRes.status})`)
        }
        const openEcdd = (Array.isArray(ecddJson.data) ? ecddJson.data : [])
          .filter((r) => ['open', 'pending_approval'].includes(String(r.status || '').toLowerCase()))
          .filter((r) => ecddMatchesVenue(r, venue))
          .slice(0, 25)
        if (!cancelled) setEcddRows(openEcdd)
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load ECDD reviews')
          setEcddRows([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [venue?.venue_id, venue?.id, venue?.code, venue?.name])

  const watchHref = `/monitor-safe/watch-list?venue=${encodeURIComponent(venue?.venue_id || venue?.id || '')}`
  const ecddHref = '/compliance-safe/ecdd'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
        <SectionHead
          icon={Eye}
          title="Active Watchlist"
          sub={
            wlTotal > 0
              ? `${wlTotal} on watchlist · ${watchlist.highRisk || 0} high risk`
              : 'No patrons currently on the venue watchlist'
          }
          right={
            <Link
              to={watchHref}
              style={{
                fontSize: 12, color: '#fff', background: C.pri, fontWeight: 700, border: 'none',
                borderRadius: 7, padding: '6px 14px', cursor: 'pointer', fontFamily: F.sans,
                display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none',
              }}
            >
              View Watchlist <ArrowRight size={11} />
            </Link>
          }
        />
        <div style={{ textAlign: 'center', padding: '20px 0', color: C.slateL, fontSize: 12 }}>
          {wlTotal === 0
            ? 'Watchlist is empty for this venue. Open ECDD reviews are listed below.'
            : `${wlTotal} patron${wlTotal === 1 ? '' : 's'} on watchlist — open the full Watch List to review.`}
        </div>
      </div>

      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
        <SectionHead
          icon={Shield}
          title="Open ECDD Reviews"
          sub={`${watchlist?.ecddOpen || ecddRows.length} active for this venue`}
          color={C.pri}
          right={
            <Link
              to={ecddHref}
              style={{
                fontSize: 12, color: C.pri, background: C.priM, fontWeight: 700,
                border: `1px solid ${C.pri}33`, borderRadius: 7, padding: '6px 14px',
                cursor: 'pointer', fontFamily: F.sans, textDecoration: 'none',
              }}
            >
              ECDD registry →
            </Link>
          }
        />
        {loading && <div style={{ textAlign: 'center', padding: '20px 0', color: C.slateL, fontSize: 12 }}>Loading…</div>}
        {!loading && !error && ecddRows.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: C.slateL, fontSize: 12 }}>No open ECDD reviews for this venue.</div>
        )}
        {!loading && !error && ecddRows.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {ecddRows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => navigate(`/compliance-safe/ecdd/${encodeURIComponent(r.id)}`)}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 120px 80px', gap: 12, alignItems: 'center',
                  width: '100%', textAlign: 'left', padding: '10px 4px', border: 'none',
                  borderBottom: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', fontFamily: F.sans,
                }}
              >
                <span>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>
                    {r.name && String(r.name).trim() && String(r.name).toLowerCase() !== 'unknown'
                      ? r.name
                      : (r.pid || r.member_id || 'Unknown')}
                  </div>
                  <div style={{ fontSize: 11, color: C.slateL, marginTop: 2 }}>
                    {r.id}
                    {r.pid ? ` · ${r.pid}` : ''}
                    {r.trigger ? ` · ${r.trigger}` : ''}
                  </div>
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.amber, textTransform: 'capitalize' }}>
                  {String(r.status || '').replace(/_/g, ' ')}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.pri, textAlign: 'right' }}>Open →</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {topPatrons.length > 0 && (
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
          <SectionHead icon={Users} title="Patrons with Open Alerts" sub="Not necessarily on watchlist" />
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(topPatrons.length, 3)}, 1fr)`, gap: 12 }}>
            {topPatrons.slice(0, 3).map((p, i) => (
              <div
                key={i}
                onClick={() => navigate(`/monitor-safe/watch-list/${encodeURIComponent(p.id || p.member_id)}`)}
                style={{ background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 9, padding: '12px 14px', cursor: 'pointer' }}
              >
                <div style={{ fontSize: 12, fontFamily: F.mono, color: C.pri, marginBottom: 1 }}>{p.id || p.member_id}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
                  {p.name && p.name !== (p.id || p.member_id) ? p.name : 'Unknown'}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.orange, marginTop: 6 }}>{p.alerts || 0} alerts</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function VenueAlertQueue({ venue, unreviewedCount }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const res = await authFetch(getLocalApiUrl('/api/alerts/list?limit=200'))
        const json = await res.json()
        if (!res.ok || !json.success) throw new Error(json.error || `Failed to load alerts (${res.status})`)
        const all = Array.isArray(json.data) ? json.data : []
        const forVenue = all.filter((a) => alertMatchesVenue(a, venue))
        const unreviewed = forVenue.filter(isUnreviewedAlert)
        const reviewed = forVenue.filter((a) => !isUnreviewedAlert(a))
        const sorted = [...unreviewed, ...reviewed].sort((a, b) => {
          const ta = new Date(a.last_updated || a.first_seen || 0).getTime()
          const tb = new Date(b.last_updated || b.first_seen || 0).getTime()
          return tb - ta
        })
        if (!cancelled) setRows(sorted.slice(0, 25))
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load alerts')
          setRows([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [venue?.venue_id, venue?.id, venue?.code, venue?.name])

  const venueQs = encodeURIComponent(venue?.code || venue?.venue_id || venue?.id || '')
  const alertsHref = venueQs ? `/monitor-safe/alerts?venue=${venueQs}` : '/monitor-safe/alerts'

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
      <SectionHead
        icon={Bell}
        title="Alert Queue"
        sub={`${unreviewedCount} unreviewed — click a row to open alert detail`}
        right={
          <Link
            to={alertsHref}
            style={{
              fontSize: 12,
              color: '#fff',
              background: C.pri,
              fontWeight: 700,
              border: 'none',
              borderRadius: 7,
              padding: '6px 14px',
              cursor: 'pointer',
              fontFamily: F.sans,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              textDecoration: 'none',
            }}
          >
            Open Alerts <ArrowRight size={11} />
          </Link>
        }
      />
      {loading && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: C.slateL, fontSize: 12 }}>Loading alerts…</div>
      )}
      {!loading && error && (
        <div style={{ textAlign: 'center', padding: '16px 0', color: C.red, fontSize: 12 }}>{error}</div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: C.slateL, fontSize: 12 }}>
          No alerts for this venue.
        </div>
      )}
      {!loading && !error && rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {rows.map((a) => {
            const sev = String(a.severity || 'medium')
            const color = severityColor(sev)
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => navigate(`/monitor-safe/alerts/${a.id}`)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '72px 1fr 110px 90px',
                  gap: 12,
                  alignItems: 'center',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 4px',
                  border: 'none',
                  borderBottom: `1px solid ${C.border}`,
                  background: 'transparent',
                  cursor: 'pointer',
                  fontFamily: F.sans,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    color,
                    background: `${color}14`,
                    border: `1px solid ${color}33`,
                    borderRadius: 999,
                    padding: '3px 8px',
                    width: 'fit-content',
                  }}
                >
                  {sev}
                </span>
                <span>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{a.rule_name || a.rule_id || 'Alert'}</div>
                  <div style={{ fontSize: 11, color: C.slateL, marginTop: 2 }}>
                    {a.member_id ? `Member ${a.member_id}` : 'Uncarded'}
                    {a.egm_house_no ? ` · EGM ${a.egm_house_no}` : ''}
                    {' · '}
                    {formatAlertWhen(a)}
                  </div>
                </span>
                <span style={{ fontSize: 12, color: C.slate, textTransform: 'capitalize' }}>{a.status || '—'}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.pri, textAlign: 'right' }}>Open →</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function VenueView({ venue, stats, pipeline, onBack }) {
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState('overview')
  const alerts = stats?.alerts || EMPTY_ALERTS
  const watchlist = stats?.watchlist || { total: 0, highRisk: 0, ecddOpen: 0, newThisWeek: 0 }
  const smr = stats?.smr || { active: 0, overdue: 0, dueSoon: 0 }
  const ruleHits = stats?.ruleHits || []
  const topPatrons = stats?.topPatrons || []
  const activityTrend = stats?.activityTrend || []
  const hasSessions = activityTrend.some((d) => (d.sessions || 0) > 0)
  const hasFlagged = activityTrend.some((d) => (d.flagged || 0) > 0)
  const pipelineBanner = dataHealthBanner(pipeline?.dataHealth)

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'alerts', label: `Alerts ${alerts.unreviewed > 0 ? `(${alerts.unreviewed})` : ''}` },
    { id: 'watchlist', label: 'Watchlist' },
    { id: 'pipeline', label: 'Data Pipeline' },
  ]

  return (
    <div>
      <div style={{ display: 'flex', gap: 3, marginBottom: 20, background: C.slateBg, borderRadius: 9, padding: 3, border: `1px solid ${C.border}` }}>
        {tabs.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            style={{ flex: 1, padding: '8px 0', background: activeSection === s.id ? '#fff' : 'transparent', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: activeSection === s.id ? 700 : 500, color: activeSection === s.id ? C.pri : C.slate, cursor: 'pointer', fontFamily: F.sans, boxShadow: activeSection === s.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
            {s.label}
          </button>
        ))}
      </div>

      {activeSection === 'overview' && (
        <div>
          {pipelineBanner && (
            <div style={{ marginBottom: 16, padding: '12px 16px', background: pipelineBanner.bg, border: `1px solid ${pipelineBanner.bd}`, borderRadius: 8, color: pipelineBanner.color, fontSize: 13, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ flex: 1 }}>{pipelineBanner.text}</span>
              <button type="button" onClick={() => setActiveSection('pipeline')} style={{ fontSize: 12, fontWeight: 700, color: pipelineBanner.color, background: 'none', border: `1px solid ${pipelineBanner.bd}`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontFamily: F.sans }}>
                View pipeline →
              </button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 22 }}>
            <StatCard label="Total Alerts" value={alerts.total} sub={`${alerts.newToday} new today`} color={C.orange} icon={Bell} trend={alerts.newToday > 0 ? 'up' : null} />
            <StatCard label="Unreviewed" value={alerts.unreviewed} sub="need CO action" color={alerts.unreviewed > 5 ? C.red : C.amber} icon={AlertTriangle} />
            <StatCard label="Watchlist" value={watchlist.total} sub={`${watchlist.highRisk} high risk`} color={C.purple} icon={Eye} />
            <StatCard label="ECDD Open" value={watchlist.ecddOpen} sub="active reviews" color={C.pri} icon={Shield} />
            <StatCard label="SMR Active" value={smr.active} sub={smr.overdue > 0 ? `${smr.overdue} OVERDUE` : smr.dueSoon > 0 ? `${smr.dueSoon} due soon` : 'all clear'} color={smr.overdue > 0 ? C.red : smr.dueSoon > 0 ? C.amber : C.green} icon={FileText} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
              <SectionHead
                icon={Activity}
                title={hasSessions ? 'Gaming Activity — 7 Days' : 'Flagged Alerts — 7 Days'}
                sub={hasSessions ? 'Imported sessions and flagged alerts' : 'Last 7 calendar days'}
              />
              {(hasSessions || hasFlagged) ? (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <AreaChart data={activityTrend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <defs>
                        {hasSessions && (
                          <linearGradient id="sessGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={C.priL} stopOpacity={0.15} />
                            <stop offset="95%" stopColor={C.priL} stopOpacity={0} />
                          </linearGradient>
                        )}
                        <linearGradient id="flagGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={C.red} stopOpacity={0.2} />
                          <stop offset="95%" stopColor={C.red} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="day" tick={{ fontSize: 12, fill: C.slateL }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: C.slateL }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, fontFamily: F.sans, borderRadius: 7, border: `1px solid ${C.border}` }} />
                      {hasSessions && (
                        <Area type="monotone" dataKey="sessions" stroke={C.priL} fill="url(#sessGrad)" strokeWidth={2} name="Sessions" dot={false} />
                      )}
                      <Area type="monotone" dataKey="flagged" stroke={C.red} fill="url(#flagGrad)" strokeWidth={2} name="Flagged alerts" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                    {hasSessions && (
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}><div style={{ width: 10, height: 3, borderRadius: 2, background: C.priL }} /><span style={{ fontSize: 12, color: C.slateL }}>Sessions</span></div>
                    )}
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}><div style={{ width: 10, height: 3, borderRadius: 2, background: C.red }} /><span style={{ fontSize: 12, color: C.slateL }}>Flagged alerts</span></div>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: C.slateL, fontSize: 12 }}>
                  {alerts.unreviewed > 0 ? (
                    <>
                      No flagged alerts in the last 7 calendar days.
                      <div style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          onClick={() => setActiveSection('alerts')}
                          style={{ fontSize: 12, fontWeight: 700, color: C.pri, background: C.priM, border: `1px solid ${C.pri}33`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontFamily: F.sans }}
                        >
                          View {alerts.unreviewed} open alert{alerts.unreviewed === 1 ? '' : 's'} →
                        </button>
                      </div>
                    </>
                  ) : (
                    'No activity in the last 7 days'
                  )}
                </div>
              )}
            </div>

            <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
              <SectionHead icon={Zap} title="Rule Hits" sub="Currently open alerts" />
              {ruleHits.length === 0
                ? <div style={{ textAlign: 'center', padding: '30px 0', color: C.slateL, fontSize: 12 }}>
                    {alerts.unreviewed > 0 ? 'Open alerts could not be grouped by rule yet' : 'No open rule hits'}
                  </div>
                : ruleHits.map((r, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < ruleHits.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 2 }}>{r.rule}</div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: r.color || C.pri, background: (r.color || C.pri) + '14', padding: '1px 7px', borderRadius: 10 }}>{r.typology}</span>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: r.color || C.pri, fontFamily: F.mono }}>{r.hits}</div>
                  </div>
                ))
              }
            </div>
          </div>

          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
            <SectionHead icon={Users} title="Top Flagged Patrons" sub="Highest open-alert activity" right={
              <Link to={`/monitor-safe/watch-list?venue=${encodeURIComponent(venue?.venue_id || venue?.id)}`} style={{ fontSize: 12, color: C.pri, fontWeight: 600, textDecoration: 'none' }}>View all →</Link>
            } />
            {topPatrons.length === 0 && (
              <div style={{ color: C.slateL, fontSize: 12, padding: '20px 0' }}>
                {alerts.unreviewed > 0
                  ? 'Open alerts for this venue are mostly uncarded (no member id) — see Alerts tab.'
                  : 'No flagged patrons with open alerts'}
              </div>
            )}
            {topPatrons.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(topPatrons.length, 3)}, 1fr)`, gap: 12 }}>
                {topPatrons.slice(0, 3).map((p, i) => (
                  <div key={i} onClick={() => navigate(`/monitor-safe/watch-list/${encodeURIComponent(p.id || p.member_id)}`)} style={{ background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 9, padding: '12px 14px', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 12, fontFamily: F.mono, color: C.pri, marginBottom: 1 }}>{p.id || p.member_id}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
                          {p.name && p.name !== (p.id || p.member_id) ? p.name : (p.name || 'Unknown')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {p.risk != null ? (
                          <>
                            <div style={{ fontSize: 16, fontWeight: 900, color: p.risk >= 90 ? C.red : p.risk >= 70 ? C.orange || '#ea7215' : p.risk >= 40 ? C.amber : C.green || '#16a34a', fontFamily: F.mono }}>{p.risk}</div>
                            <div style={{ fontSize: 12, color: C.slateL }}>risk</div>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.orange }}>{p.alerts || 0} alerts</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeSection === 'alerts' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Critical', value: alerts.critical, color: C.red, bg: C.redBg },
              { label: 'High', value: alerts.high, color: C.orange, bg: C.orangeBg },
              { label: 'Medium', value: alerts.medium, color: C.amber, bg: C.amberBg },
              { label: 'Low', value: alerts.low, color: C.green, bg: C.greenBg },
            ].map((s, i) => (
              <div key={i} style={{ background: s.bg, border: `1px solid ${s.color}33`, borderRadius: 9, padding: '14px 16px' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: s.color, fontFamily: F.mono }}>{s.value}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: s.color }}>{s.label}</div>
              </div>
            ))}
          </div>
          <VenueAlertQueue venue={venue} unreviewedCount={alerts.unreviewed} />
        </div>
      )}

      {activeSection === 'watchlist' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Total on Watchlist', value: watchlist.total, color: C.purple, icon: Eye },
              { label: 'High Risk', value: watchlist.highRisk, color: C.red, icon: AlertTriangle },
              { label: 'ECDD Open', value: watchlist.ecddOpen, color: C.pri, icon: Shield },
              { label: 'Added This Week', value: watchlist.newThisWeek, color: C.orange, icon: TrendingUp },
            ].map((s, i) => (
              <StatCard key={i} label={s.label} value={s.value} color={s.color} icon={s.icon} />
            ))}
          </div>
          <VenueWatchlistPanel venue={venue} watchlist={watchlist} topPatrons={topPatrons} />
        </div>
      )}

      {activeSection === 'pipeline' && (
        <DataPipelinePanel pipeline={pipeline} />
      )}
    </div>
  )
}

/* ══════════════════════════════════════════════════════
   ROOT
══════════════════════════════════════════════════════ */
export default function MonitorDashboardV2() {
  const navigate = useNavigate()
  const [venues, setVenues] = useState([])
  const [venueStats, setVenueStats] = useState([])
  const [portfolioAlerts, setPortfolioAlerts] = useState(EMPTY_ALERTS)
  const [portfolioWatchlist, setPortfolioWatchlist] = useState({ total: 0, highRisk: 0 })
  const [pipeline, setPipeline] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [selectedVenue, setSelectedVenue] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/monitor-safe/dashboard-summary?days=7'))
      if (!res.ok) {
        throw new Error(await readApiAccessMessage(res))
      }
      const json = await res.json()
      if (!json.success) {
        throw new Error(json.error || 'Dashboard summary failed')
      }

      const { portfolio, venues: venueRows, pipeline: pipelineData } = json.data || {}
      setPortfolioAlerts(portfolio?.alerts || EMPTY_ALERTS)
      setPortfolioWatchlist(portfolio?.watchlist || { total: 0, highRisk: 0 })
      setPipeline(pipelineData || null)

      const rows = Array.isArray(venueRows) ? venueRows : []
      setVenues(rows.map((v) => ({
        venue_id: v.venueId,
        id: v.venueId,
        name: v.name,
        code: v.code,
      })))
      setVenueStats(rows.map(mapVenueStatsRow))
    } catch (e) {
      console.error('MonitorDashboardV2 load error:', e)
      setLoadError(e.message || 'Failed to load dashboard')
      setVenues([])
      setVenueStats([])
      setPortfolioAlerts(EMPTY_ALERTS)
      setPortfolioWatchlist({ total: 0, highRisk: 0 })
      setPipeline(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const selectedStats = selectedVenue ? venueStats.find(s => s.venueId === selectedVenue.venue_id || s.venueId === selectedVenue.id) : null
  const totalCritical = selectedVenue
    ? (selectedStats?.alerts?.critical || 0)
    : (portfolioAlerts?.critical || 0)

  if (loading) {
    return (
      <div style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.sans, color: C.slate }}>
        Loading dashboard…
      </div>
    )
  }

  return (
    <div style={{ width: '100%', minHeight: '100%', fontFamily: F.sans, padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {selectedVenue && (
            <button onClick={() => setSelectedVenue(null)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, color: C.ink, cursor: 'pointer', fontFamily: F.sans, fontWeight: 600 }}>
              <ChevronLeft size={14} /> All Venues
            </button>
          )}
          <span style={{ fontSize: 18, fontWeight: 800, color: C.ink }}>
            {selectedVenue ? selectedVenue.name : 'Monitoring Dashboard'}
          </span>
          {totalCritical > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 20, fontSize: 12, fontWeight: 700, color: C.red }}>
              <AlertTriangle size={12} /> {totalCritical} critical
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={load} style={{ padding: '6px 14px', background: C.pri, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 5 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {loadError && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 8, color: C.red, fontSize: 13 }}>
          <strong>Access Denied.</strong> {loadError}
        </div>
      )}

      {selectedVenue
        ? <VenueView venue={selectedVenue} stats={selectedStats} pipeline={pipeline} onBack={() => setSelectedVenue(null)} />
        : <AllVenuesView
            venues={venues}
            venueStats={venueStats}
            portfolioAlerts={portfolioAlerts}
            portfolioWatchlist={portfolioWatchlist}
            pipeline={pipeline}
            onSelect={setSelectedVenue}
          />}
    </div>
  )
}
